import { createClient } from '@supabase/supabase-js';
import { SUPABASE_CONFIG } from '../config/supabase';
import { Database } from '../types/supabase-types';

const supabaseUrl = (SUPABASE_CONFIG.url || '').toString().trim();
const supabaseAnonKey = (SUPABASE_CONFIG.anonKey || '').toString().trim();
const allowMock =
  import.meta.env.VITE_ALLOW_MOCK_SUPABASE === 'true' &&
  import.meta.env.MODE !== 'production';

function createMockSupabase() {
  console.warn(
    '[supabase] Mock client active — set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, or disable VITE_ALLOW_MOCK_SUPABASE'
  );

  let currentSession: { user: { id: string; email: string }; access_token: string } | null = null;
  let authCallback: ((event: string, session: unknown) => void) | null = null;
  const subscription = { unsubscribe: () => {} };

  return {
    auth: {
      getSession: async () => ({ data: { session: currentSession } }),
      getUser: async () => ({ data: { user: currentSession?.user ?? null }, error: null }),
      onAuthStateChange: (callback: (event: string, session: unknown) => void) => {
        authCallback = callback;
        setTimeout(() => callback('INITIAL_SESSION', currentSession), 0);
        return { data: { subscription } };
      },
      signInWithPassword: async (credentials: { email: string }) => {
        const mockUser = { id: 'mock-user-id', email: credentials.email, user_metadata: {} };
        currentSession = { user: mockUser, access_token: 'mock-token' };
        authCallback?.('SIGNED_IN', currentSession);
        return { error: null, data: { user: mockUser, session: currentSession } };
      },
      signUp: async (credentials: { email: string; options?: { data?: Record<string, unknown> } }) => ({
        error: null,
        data: { user: { id: 'mock-user-id', email: credentials.email }, session: null },
      }),
      signOut: async () => {
        currentSession = null;
        authCallback?.('SIGNED_OUT', null);
        return { error: null };
      },
      resetPasswordForEmail: async () => ({ error: null }),
      updateUser: async (updates: Record<string, unknown>) => ({
        error: null,
        data: { user: { id: 'mock-user-id', ...updates } },
      }),
    },
    from: () => ({
      select: () => ({
        eq: () => ({
          single: async () => ({ data: null, error: { code: 'PGRST116' } }),
          maybeSingle: async () => ({ data: null, error: null }),
        }),
      }),
      insert: () => ({
        select: () => ({
          single: async () => ({ data: null, error: { message: 'Mock client' } }),
        }),
      }),
      update: () => ({
        eq: () => ({
          select: () => ({
            single: async () => ({ data: null, error: { message: 'Mock client' } }),
          }),
        }),
      }),
    }),
    storage: { from: () => ({ upload: async () => ({ error: { message: 'Mock client' } }) }) },
    functions: { invoke: async () => ({ data: null, error: { message: 'Mock client' } }) },
  } as unknown as ReturnType<typeof createClient<Database>>;
}

function createRealSupabase() {
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'sb-auth-token',
      flowType: 'pkce',
    },
    global: {
      headers: { 'X-Client-Info': 'supabase-js-web' },
    },
    db: { schema: 'public' },
  });
}

if (!supabaseUrl || !supabaseAnonKey) {
  if (!allowMock) {
    throw new Error(
      'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Configure .env or set VITE_ALLOW_MOCK_SUPABASE=true for offline UI only.'
    );
  }
}

export const supabase =
  supabaseUrl && supabaseAnonKey ? createRealSupabase() : createMockSupabase();

export default supabase;
